import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberLevelListReport } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import './member-level-report.css';

export function MemberLevelReport() {
  const { t } = useTranslation('member');
  const session = useAuthStore((s) => s.sessionRevision);
  const identity = useAuthStore((s) => `${s.user?.id}:${s.sessionRevision}`);
  const { data: capabilities } = useQuery({
    queryKey: ['member-admin-tools', identity],
    queryFn: () => api.get<{ reportEnabled: boolean }>('/members/admin-tools'),
  });
  const [report, setReport] = useState<MemberLevelListReport | null>(null);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setReport(null);
    setKey(crypto.randomUUID());
    setError('');
  }, [identity]);
  useEffect(() => {
    if (!report) return;
    const timer = window.setTimeout(
      () => {
        setReport(null);
        setKey(crypto.randomUUID());
        setError(t('adminTools.expired'));
      },
      Math.max(0, Date.parse(report.generatedAt) + 15 * 60000 - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [report, t]);
  if (!capabilities?.reportEnabled) return null;
  async function generate() {
    setBusy(true);
    setError('');
    try {
      const next = await api.post<MemberLevelListReport>('/members/reports/level-list', {
        operationKey: key,
        format: 'print',
      });
      if (useAuthStore.getState().sessionRevision === session) setReport(next);
    } catch (e) {
      if (useAuthStore.getState().sessionRevision !== session) return;
      const message = e instanceof Error ? e.message : t('adminTools.failed');
      if (message.includes('REPORT_EXPIRED')) {
        setReport(null);
        setKey(crypto.randomUUID());
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  }
  const tables = report && (
    <>
      {report.groups.map((group) => (
        <table key={group.levelId} className="member-level-table">
          <thead>
            <tr>
              <th colSpan={4}>
                {t('adminTools.title')} — {group.displayName} ({group.rows.length})<br />
                {t('adminTools.scope')} ·{' '}
                {new Date(report.generatedAt).toLocaleString('zh-TW', {
                  timeZone: report.timezone,
                })}{' '}
                {report.timezone}
              </th>
            </tr>
            <tr>
              <th>{t('memberNo')}</th>
              <th>{t('nickname')}</th>
              <th>{t('adminTools.phoneSuffix')}</th>
              <th>{t('adminTools.status')}</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => (
              <tr key={row.memberNo}>
                <td>{row.memberNo}</td>
                <td>{row.nickname}</td>
                <td>{row.phoneSuffix ?? '—'}</td>
                <td>{t(`status_${row.status}`)}</td>
              </tr>
            ))}
            {!group.rows.length && (
              <tr>
                <td colSpan={4}>{t('adminTools.empty')}</td>
              </tr>
            )}
          </tbody>
        </table>
      ))}
    </>
  );
  return (
    <section className="mb-4 rounded-lg border border-base-300 p-4">
      <p className="mb-2 text-sm">{t('adminTools.scope')}</p>
      <button className="btn btn-outline" disabled={busy} onClick={() => void generate()}>
        {t('adminTools.generate')}
      </button>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      {report && (
        <>
          <p className="my-2 text-sm">{t('adminTools.generated', { count: report.total })}</p>
          <button className="btn btn-primary mr-2" onClick={() => window.print()}>
            {t('adminTools.print')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => {
              setReport(null);
              setKey(crypto.randomUUID());
            }}
          >
            {t('adminTools.close')}
          </button>
          <div className="overflow-auto">{tables}</div>
          {createPortal(<div className="member-level-print">{tables}</div>, document.body)}
        </>
      )}
    </section>
  );
}
